/**
 * WangTilerNode - Constraint-based tile placement using Wang tiles
 *
 * Each tile type has 4 edge colors (N, E, S, W). The grid is filled
 * left-to-right, top-to-bottom, ensuring adjacent edges match.
 * Outputs position arrays + tileType + rotation for Repeater consumption.
 */

import { BaseNode } from '@nodes/BaseNode';
import { Domain } from '@nodes/Endpoint';
import { params, internals } from '@/nodes/TypedEndpoints';
import type { NodeDef } from '@runtime/IRuntime';
import type { Payload } from '@graph/Payload';
import { seededRandom } from '../../randomUtils';

// ─────────────────────────────────────────────────────────────
// Tile definitions
// ─────────────────────────────────────────────────────────────

/** Edge indices: 0=N, 1=E, 2=S, 3=W */
interface TileDef {
  edges: [number, number, number, number]; // [N, E, S, W]
}

/** Generate a tile set with seeded random edge colors */
function generateTileSet(
  tileTypes: number,
  edgeColors: number,
  seed: number
): TileDef[] {
  const rng = seededRandom(seed);
  const tiles: TileDef[] = [];

  for (let i = 0; i < tileTypes; i++) {
    tiles.push({
      edges: [
        Math.floor(rng() * edgeColors),
        Math.floor(rng() * edgeColors),
        Math.floor(rng() * edgeColors),
        Math.floor(rng() * edgeColors),
      ],
    });
  }
  return tiles;
}

/** Rotate a tile's edges by 90-degree increments (0-3) */
function rotateEdges(edges: [number, number, number, number], rotations: number): [number, number, number, number] {
  const r = ((rotations % 4) + 4) % 4;
  if (r === 0) return edges;
  // Rotate CW: N→E, E→S, S→W, W→N
  const [n, e, s, w] = edges;
  if (r === 1) return [w, n, e, s];
  if (r === 2) return [s, w, n, e];
  return [e, s, w, n]; // r === 3
}

// ─────────────────────────────────────────────────────────────
// Endpoints
// ─────────────────────────────────────────────────────────────

const wangTilerEndpoints = {
  ...params({
    cols: { value: 5, min: 1, max: 32, step: 1 },
    rows: { value: 5, min: 1, max: 32, step: 1 },
    spacingX: { value: 10, min: 0.1, max: 200, step: 0.1 },
    spacingZ: { value: 10, min: 0.1, max: 200, step: 0.1 },
    tileTypes: { value: 3, min: 1, max: 8, step: 1, label: 'Tile Types' },
    edgeColors: { value: 2, min: 1, max: 4, step: 1, label: 'Edge Colors' },
    seed: { value: 42, min: 0, max: 9999, step: 1 },
  }),
  ...internals({
    posX: { dataType: 'array', isOutput: true, label: 'X' },
    posZ: { dataType: 'array', isOutput: true, label: 'Z' },
    tileType: { dataType: 'array', isOutput: true, label: 'Type' },
    rotY: { dataType: 'array', isOutput: true, label: 'Rot Y' },
    pattern2d: { dataType: 'array', isOutput: true, label: '2D' },
  }),
};

// ─────────────────────────────────────────────────────────────
// Node
// ─────────────────────────────────────────────────────────────

class WangTilerNode extends BaseNode<typeof wangTilerEndpoints> {

  initialize(): void {
    this.compute();
  }

  onParamChanged(_payload: Payload, _endpointId: string): void {
    this.compute();
  }

  private compute(): void {
    const cols = this.params.cols.value;
    const rows = this.params.rows.value;
    const spacingX = this.params.spacingX.value;
    const spacingZ = this.params.spacingZ.value;
    const numTileTypes = this.params.tileTypes.value;
    const numEdgeColors = this.params.edgeColors.value;
    const seed = this.params.seed.value;

    // Generate tile set from seed
    const tiles = generateTileSet(numTileTypes, numEdgeColors, seed);

    // Build all candidates: each tile type × 4 rotations
    const candidates: { type: number; rot: number; edges: [number, number, number, number] }[] = [];
    for (let t = 0; t < tiles.length; t++) {
      for (let r = 0; r < 4; r++) {
        candidates.push({
          type: t,
          rot: r,
          edges: rotateEdges(tiles[t].edges, r),
        });
      }
    }

    // Grid placement: row-major, each cell stores chosen candidate
    const grid: (typeof candidates[0])[] = new Array(rows * cols);
    const rng = seededRandom(seed + 7919); // Offset seed for placement randomness

    // Center the grid
    const offsetX = ((cols - 1) * spacingX) / 2;
    const offsetZ = ((rows - 1) * spacingZ) / 2;

    const posXArr: number[] = [];
    const posZArr: number[] = [];
    const tileTypeArr: number[] = [];
    const rotYArr: number[] = [];

    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        const idx = row * cols + col;

        // Determine constraints from neighbors
        const needW = col > 0 ? grid[idx - 1].edges[1] : -1;    // left neighbor's E edge
        const needN = row > 0 ? grid[idx - cols].edges[2] : -1;  // top neighbor's S edge

        // Filter candidates matching constraints
        const valid = candidates.filter(c => {
          if (needW >= 0 && c.edges[3] !== needW) return false; // W must match left's E
          if (needN >= 0 && c.edges[0] !== needN) return false; // N must match top's S
          return true;
        });

        // Pick from valid candidates (fallback to all if none match)
        const pool = valid.length > 0 ? valid : candidates;
        const pick = pool[Math.floor(rng() * pool.length)];
        grid[idx] = pick;

        posXArr.push(col * spacingX - offsetX);
        posZArr.push(row * spacingZ - offsetZ);
        tileTypeArr.push(pick.type);
        rotYArr.push(pick.rot * (Math.PI / 2));
      }
    }

    // 2D grid of tile types for Pattern2DViz
    const pattern: number[][] = [];
    for (let row = 0; row < rows; row++) {
      pattern.push(tileTypeArr.slice(row * cols, (row + 1) * cols));
    }

    this.internals.posX.set(posXArr);
    this.internals.posZ.set(posZArr);
    this.internals.tileType.set(tileTypeArr);
    this.internals.rotY.set(rotYArr);
    this.internals.pattern2d.set(pattern);
  }
}

// ─────────────────────────────────────────────────────────────
// Node Definition
// ─────────────────────────────────────────────────────────────

export const wangTilerNodeDef: NodeDef<WangTilerNode> = {
  type: 'WangTiler',
  shortName: 'wang',
  info: 'Constraint-based tile placement using Wang tiles. Adjacent edges must match colours.',
  category: 'Layout',
  domain: Domain.Data,
  endpoints: wangTilerEndpoints,
  ctor: WangTilerNode,
};
